import React, { useState } from 'react';

const ResourceConnectionTest = () => {
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runConnectionTest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Running connection test...');
      const response = await fetch('/api/resources/test-connection');
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      setTestResult(data);
    } catch (err) {
      console.error('Connection test error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const initializeResourceTables = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Initializing resource tables via API...');
      const response = await fetch('/api/resources/init-tables', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
      }
      
      const data = await response.json();
      setTestResult(data);
      
      // Run connection test after initializing
      if (data.success) {
        setTimeout(runConnectionTest, 1000);
      }
    } catch (err) {
      console.error('Table initialization error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Additional direct method for client-side table creation
  const createTablesDirectly = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use direct Supabase client
      const { supabase } = await import('../../utils/supabaseClient');
      
      // Try to create resource_categories table if needed
      const { error } = await supabase.rpc('create_resources_tables');
      
      if (error) {
        console.error('RPC error:', error);
        setError(`Failed to create tables: ${error.message}`);
      } else {
        setTestResult({ success: true, message: 'Tables created successfully!' });
        
        // Run connection test after initializing
        setTimeout(runConnectionTest, 1000);
      }
    } catch (err) {
      console.error('Direct table creation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <h2 className="text-lg font-semibold mb-4">Resources Connection Test</h2>
      
      <div className="flex flex-wrap gap-2 mb-4">
        <button 
          onClick={runConnectionTest}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Testing...' : 'Test Connection'}
        </button>
        
        <button 
          onClick={initializeResourceTables}
          disabled={loading}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'Initializing...' : 'Server Init Tables'}
        </button>
        
        <button 
          onClick={createTablesDirectly}
          disabled={loading}
          className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Client Init Tables'}
        </button>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded">
          <p className="font-medium">Error:</p>
          <p className="whitespace-pre-wrap break-words">{error}</p>
        </div>
      )}
      
      {testResult && (
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <h3 className="font-medium mb-2">Test Result:</h3>
          <pre className="text-sm overflow-auto max-h-60 whitespace-pre-wrap break-words">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ResourceConnectionTest; 